import { Plane, Vector3 } from "three";
import CommandIdEnum from "../enums/CommandIdEnums";
import CommandStack from "../logic/CommandStack";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import Document from "../model/document/Document";
import LengthDimensionData from "../model/sceneObjectData/LengthDimensionData";
import GeometryMoveEditCmd from "./GeometryMoveEditCmd";

/**
 * Move edit for a LengthDimension's start/end point. In addition to the default
 * single-origin reference axes, snaps to axes through both the start and end
 * points, the dimension's own axis, and the axis perpendicular to it.
 */
export default class GeometryDimensionEndpointMoveCmd extends GeometryMoveEditCmd {

    constructor(
        commandStack: CommandStack,
        canvasInputListener: ICanvasInputListener,
        document: Document,
    ) {
        super(commandStack, canvasInputListener, document);
        this.id = CommandIdEnum.GeometryDimensionEndpointMove;
    }

    override activate(): void {
        const data = this.ownerSceneObject.data as LengthDimensionData;
        // data.start/end are in local (parent) space; convert to world space to match
        // editHandleData.position/plane, which SelectCmd.showHandles() already transforms.
        const start = data.start.clone().applyMatrix4(this.parentWorldMatrix);
        const end = data.end.clone().applyMatrix4(this.parentWorldMatrix);

        const dimensionAxis = end.clone().sub(start);
        if (dimensionAxis.lengthSq() < 1e-10) dimensionAxis.set(1, 0, 0);
        else dimensionAxis.normalize();

        const planeNormal = this.editHandleData.plane?.normal.clone() ?? new Vector3(0, 0, 1);
        const perpendicular = planeNormal.clone().cross(dimensionAxis);
        if (perpendicular.lengthSq() < 1e-10) perpendicular.set(0, 0, 1);
        else perpendicular.normalize();

        const vZ = new Vector3(0, 0, 1);
        const vX = new Vector3(1, 0, 0).applyAxisAngle(vZ, this.modelRotationOffset);
        const vY = vX.clone().applyAxisAngle(vZ, Math.PI / 2);
        const worldAxes = [vX, vY, vZ];

        this.refAxisHelper.enable();
        this.refAxisHelper.setAxes([
            ...worldAxes.map(direction => ({ origin: start.clone(), direction })),
            ...worldAxes.map(direction => ({ origin: end.clone(), direction })),
            { origin: start.clone(), direction: dimensionAxis },
            { origin: start.clone(), direction: perpendicular },
            { origin: end.clone(), direction: dimensionAxis },
            { origin: end.clone(), direction: perpendicular },
        ]);

        if (this.editHandleData.plane) {
            this.showPlaneGrid(this.editHandleData.position.clone(), this.editHandleData.plane.normal.clone());
        }
        this.showSwitchPlaneButton();
    }

    override deactivate(): void {
        this.disableReferenceLine();
    }

    override onSwitchPlane(): void {
        super.onSwitchPlane();
        const normalToUse = this.planeGridHelper.activeNormal;
        if (!normalToUse) return;
        this.canvasInputListener.setTargetPlane(
            new Plane().setFromNormalAndCoplanarPoint(normalToUse.clone(), this.editHandleData.position.clone())
        );
    }
}
